import {
  User,
  Users,
  FileText,
  UserCheck,
  ClipboardCheck,
  CheckCircle2,
  Circle,
  ArrowLeft,
  Send,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { PRFSectionStatus } from '../../types/prf';

interface MinorInjurySection {
  id: string;
  name: string;
  icon: typeof User;
  status: PRFSectionStatus;
  color: string;
  bgColor: string;
  borderColor: string;
}

interface MinorInjuryMainScreenProps {
  onSectionSelect: (sectionId: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  sectionStatuses?: Record<string, PRFSectionStatus>;
  submitting?: boolean;
}

export default function MinorInjuryMainScreen({
  onSectionSelect,
  onBack,
  onSubmit,
  sectionStatuses = {},
  submitting = false,
}: MinorInjuryMainScreenProps) {
  const sections: MinorInjurySection[] = [
    {
      id: 'patientDetails',
      name: 'Patient Details',
      icon: User,
      status: sectionStatuses.patientDetails || 'not_started',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
    },
    {
      id: 'nokDetails',
      name: 'NOK Details',
      icon: Users,
      status: sectionStatuses.nokDetails || 'not_started',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
    },
    {
      id: 'presentingComplaint',
      name: 'Presenting Complaint',
      icon: FileText,
      status: sectionStatuses.presentingComplaint || 'not_started',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
    },
    {
      id: 'attending',
      name: 'Attending',
      icon: UserCheck,
      status: sectionStatuses.attending || 'not_started',
      color: 'text-pink-400',
      bgColor: 'bg-pink-500/10',
      borderColor: 'border-pink-500/30',
    },
    {
      id: 'closingDetails',
      name: 'Closing Details',
      icon: ClipboardCheck,
      status: sectionStatuses.closingDetails || 'not_started',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
    },
  ];

  const getStatusIcon = (status: PRFSectionStatus) => {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />;
      case 'incomplete':
        return <Circle className="w-4 h-4 text-yellow-400" />;
      default:
        return <Circle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const completedSections = sections.filter((s) => s.status === 'complete').length;
  const totalSections = sections.length;
  const progressPercentage = (completedSections / totalSections) * 100;
  const allComplete = completedSections === totalSections;

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-foreground">Minor Injury Form</h1>
              <p className="text-xs text-muted-foreground">Short patient report</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold text-foreground">
              {completedSections} / {totalSections} Complete
            </p>
            <div className="w-24 h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
              <div
                className="h-full bg-amber-400 transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => onSectionSelect(section.id)}
                className={cn(
                  'relative p-4 rounded-xl border-2 transition-all duration-200',
                  'hover:scale-[1.02] active:scale-[0.98]',
                  'flex flex-col items-center gap-2 text-center',
                  section.bgColor,
                  section.borderColor,
                  'hover:shadow-lg',
                )}
              >
                <div className="absolute top-2 right-2">{getStatusIcon(section.status)}</div>
                <div className={cn('p-3 rounded-lg', section.bgColor)}>
                  <Icon className={cn('w-6 h-6', section.color)} />
                </div>
                <span className="text-sm font-semibold text-foreground">{section.name}</span>
                <span className="text-[10px] text-muted-foreground capitalize">
                  {section.status.replace('_', ' ')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!allComplete || submitting}
          className={cn(
            'w-full py-3 rounded-lg font-semibold text-sm transition-all',
            'flex items-center justify-center gap-2',
            allComplete && !submitting
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : 'bg-muted text-muted-foreground cursor-not-allowed',
          )}
        >
          <Send className="w-4 h-4" />
          {submitting ? 'Saving...' : 'Submit minor injury form'}
          {!allComplete && (
            <span className="text-xs">({totalSections - completedSections} sections remaining)</span>
          )}
        </button>
      </div>
    </div>
  );
}
